import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import Popup from '../../Popup/Popup.module';
import BuildingSaleBoitageTutorial from './components/BuildingSaleBoitageTutorial';
import GeneralAssemblyManagerTutorial from './components/GeneralAssemblyManagerTutorial';
import GeneralAssemblyReferentTutorial from './components/GeneralAssemblyReferentTutorial';
import ReferentAppointmentsTutorial from './components/ReferentAppointmentsTutorial';
import ReferentProspectingTutorial from './components/ReferentProspectingTutorial';
import SCIsTutorial from './components/SCIsTutorial';

interface DismissibleModuleTutorialPopupProps {
  tutorial: string; // e.g. 'general-assembly'
  label?: string;
  // Optional render-prop to render a custom trigger button/link.
  // If provided, the default trigger button is not rendered.
  renderTrigger?: (open: () => void) => React.ReactNode;
  // Optional container className to wrap the trigger. Defaults to "flex justify-end mb-2".
  containerClassName?: string;
  // Controlled mode (optional): when provided, the popup open state is controlled externally
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  // Optional external tutorial setter if the parent wants to switch tutorials
  setTutorial?: (tutorial: string) => void;
}

const tutorialRegistry: Record<string, { title: React.ReactNode; Component: React.FC }> = {
  'general-assembly-referent': {
    title: 'Module Assemblée Générale — Tutoriel',
    Component: GeneralAssemblyReferentTutorial,
  },
  'general-assembly-manager': {
    title: 'Module Assemblée Générale (Gestionnaire) — Tutoriel',
    Component: GeneralAssemblyManagerTutorial,
  },
  'monitoring-scis-overall': {
    title: 'Module SCI — Tutoriel',
    Component: SCIsTutorial,
  },
  'communication-building-sale-boitage': {
    title: <span><span role="img" aria-label="book">📚</span> Guide d'utilisation</span>,
    Component: BuildingSaleBoitageTutorial,
  },
  'referent-prospecting': {
    title: <span><span role="img" aria-label="book">📚</span> Guide d'utilisation</span>,
    Component: ReferentProspectingTutorial,
  },
  'referent-appointments': {
    title: <span><span role="img" aria-label="book">📚</span> Guide d'utilisation</span>,
    Component: ReferentAppointmentsTutorial,
  },
};

function DismissibleModuleTutorialPopup({ tutorial, label, renderTrigger, containerClassName, isOpen: isOpenProp, setIsOpen: setIsOpenProp }: DismissibleModuleTutorialPopupProps) {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isManuallyOpened, setIsManuallyOpened] = useState(false);

  const isControlled = typeof isOpenProp === 'boolean' && typeof setIsOpenProp === 'function';
  const isOpen = isControlled ? (isOpenProp as boolean) : internalIsOpen;

  const { title, Component } = useMemo(() => {
    const fallback = {
      title: 'Tutoriel',
      Component: (() => <div className="text-sm text-gray-700">Aucun contenu de tutoriel défini.</div>) as React.FC,
    };
    return tutorialRegistry[tutorial] ?? fallback;
  }, [tutorial]);

  const tutorialKey = useMemo(() => `tutorial.${tutorial}`, [tutorial]);

  useEffect(() => {
    if (isControlled) { return; }
    if (!jwt) { return; }
    fetch('/api/users/me/tutorials', JWTHeader(jwt))
      .then((r) => (r.ok ? r.json() : {}))
      .then((prefs: Record<string, boolean>) => {
        const flag = prefs ? prefs[tutorialKey] : undefined;
        if (flag !== true) {
          setInternalIsOpen(true);
          setIsManuallyOpened(false); // Auto-opened on first visit
        }
      })
      .catch(() => { /* silent fail */ });
  }, [isControlled, jwt, tutorialKey]);

  // In controlled mode, mirror the manual-open semantics for consistent UI
  useEffect(() => {
    if (!isControlled) { return; }
    if (isOpen) {
      setIsManuallyOpened(true);
      setDontShowAgain(false);
    } else {
      setIsManuallyOpened(false);
    }
  }, [isControlled, isOpen]);

  const handleManualOpen = () => {
    if (isControlled) {
      (setIsOpenProp as (open: boolean) => void)(true);
    } else {
      setInternalIsOpen(true);
    }
    setIsManuallyOpened(true);
    setDontShowAgain(false); // Reset checkbox when manually opened
  };

  const handleClose = () => {
    // Only persist the "dont show again" when auto-opened (not manually)
    // and when the component manages its own open state
    if (!isControlled && jwt && dontShowAgain && !isManuallyOpened) {
      fetch('/api/users/me/tutorials', {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [tutorialKey]: true }),
      }).catch(() => { /* silent fail */ });
    }
    if (isControlled) {
      (setIsOpenProp as (open: boolean) => void)(false);
    } else {
      setInternalIsOpen(false);
    }
    setIsManuallyOpened(false);
  };

  return (
    <>
      <div className={containerClassName ?? 'mb-2 flex justify-end'}>
        {renderTrigger ? (
          renderTrigger(handleManualOpen)
        ) : (
          <button
            onClick={handleManualOpen}
            className="h-10 rounded border border-rgpd-white/20 !bg-zinc-700/30 px-4 py-2 text-white shadow-md"
          >
            <span role="img" aria-label="book">📚</span>{' '}{label ?? 'Tutoriel'}
          </button>
        )}
      </div>
      <Popup
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        additionalClass="w-full max-w-5xl"
      >
        <div className="space-y-6 p-2 text-sm text-gray-700">
          <Component />
          <div className="flex items-center gap-2 border-t pt-4">
            {!isManuallyOpened && (
              <>
                <input
                  id={`tutorial-dont-${tutorial}`}
                  type="checkbox"
                  className="h-4 w-4"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                <label htmlFor={`tutorial-dont-${tutorial}`} className="text-sm">Ne plus afficher ce tutoriel au démarrage</label>
              </>
            )}
            <div className="flex-1" />
            <button
              onClick={handleClose}
              className="rounded bg-rgpd-blue-dark px-4 py-2 text-sm text-white shadow-md"
            >
              {isManuallyOpened ? 'Fermer' : 'Commencer'}
            </button>
          </div>
        </div>
      </Popup>
    </>
  );
}

export default DismissibleModuleTutorialPopup;
